// Display formatting + honest roll-ups for the Scheduler surface (Signal re-layout). Everything here is
// display math over the module-recorded health projection (G-182): lifecycle counts, local clock/date
// formatting, and the placement key that puts a SERVER-projected occurrence or a RECORDED run on its
// calendar day. No recurrence arithmetic happens client-side — the occurrences are the deterministic,
// bounded projection the scheduler module computed (G-110).

import type { RunRecordRow, ScheduleHealthRow, ScheduleHealthScheduleRow } from '../projections.ts';

/** The state-derived vocabulary for projected dates across list, calendar and detail surfaces. */
export interface ScheduleProjectionPresentation {
  readonly tone: 'due' | 'planned';
  readonly entryStateText: string;
  readonly listCaption: string;
  readonly sectionLabel: string;
  readonly nonExecutingNote: string | null;
}

const PROJECTION_PRESENTATION: Readonly<
  Record<ScheduleHealthScheduleRow['state'], ScheduleProjectionPresentation>
> = {
  active: {
    tone: 'due',
    entryStateText: 'due',
    listCaption: 'next run',
    sectionLabel: 'Next runs',
    nonExecutingNote: null,
  },
  draft: {
    tone: 'planned',
    entryStateText: 'projected only · draft inactive',
    listCaption: 'draft projection',
    sectionLabel: 'Projected dates',
    nonExecutingNote: 'These dates are projections only. This draft will not run until activated.',
  },
  paused: {
    tone: 'planned',
    entryStateText: 'projected only · paused',
    listCaption: 'paused projection',
    sectionLabel: 'Projected dates',
    nonExecutingNote: 'These dates are projections only. This routine will not run while paused.',
  },
  finished: {
    tone: 'planned',
    entryStateText: 'finished · not running',
    listCaption: 'no future runs',
    sectionLabel: 'Future dates',
    nonExecutingNote: 'This routine is finished and has no future runs.',
  },
  archived: {
    tone: 'planned',
    entryStateText: 'archived · not running',
    listCaption: 'archived',
    sectionLabel: 'Future dates',
    nonExecutingNote:
      'This routine is archived: it has no next run and keeps its history. Restore it to bring it back paused.',
  },
};

/** One state-derived vocabulary for projected dates across list, calendar and detail surfaces. */
export function scheduleProjectionPresentation(
  state: ScheduleHealthScheduleRow['state'],
): ScheduleProjectionPresentation {
  return PROJECTION_PRESENTATION[state];
}

/** True when a row's module-recorded health needs a human look. */
export function needsAttention(row: ScheduleHealthRow): boolean {
  return row.health === 'failing' || row.health === 'interrupted';
}

/** Lifecycle counts across the human Schedules (toolbar stamp + list tab counts). */
export function stateCounts(schedules: readonly ScheduleHealthScheduleRow[]): {
  readonly live: number;
  readonly paused: number;
  readonly draft: number;
  readonly finished: number;
  readonly archived: number;
} {
  return {
    live: schedules.filter((row) => row.state === 'active').length,
    paused: schedules.filter((row) => row.state === 'paused').length,
    draft: schedules.filter((row) => row.state === 'draft').length,
    finished: schedules.filter((row) => row.state === 'finished').length,
    archived: schedules.filter((row) => row.state === 'archived').length,
  };
}

/** A sanitised actor ref (`human:<id>` / `agent` / `system`) as the board says it — never an internal id. */
export function actorLabel(actorRef: string): string {
  if (actorRef === 'agent') return 'Ritona';
  if (actorRef === 'system') return 'the platform';
  return 'a person';
}

/** "Revision 3 · edited by Ritona, as asked by a person, at 09:41 on 2026-09-02" — the detail panel's
 *  provenance line. A routine never revised since creation says so rather than inventing an editor. */
export function editedByLine(row: ScheduleHealthScheduleRow): string {
  const revision = `Revision ${String(row.definitionRevision)}`;
  const last = row.lastRevision;
  if (last === null) return `${revision} · not edited since it was created`;
  const who =
    last.revisedByActor === 'agent' && last.requestedByUserId !== null
      ? 'Ritona, as asked by a person,'
      : actorLabel(last.revisedByActor);
  return `${revision} · edited by ${who} at ${fmtClock(last.revisedAt)} on ${last.revisedAt.slice(0, 10)}`;
}

/** HH:MM local from an ISO instant; anything unparsable passes through (display only). */
export function fmtClock(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

/** The local YYYY-MM-DD of a Date (the calendar's placement key — browser-local, display only). */
export function localIsoDate(date: Date): string {
  const y = String(date.getFullYear());
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** The local YYYY-MM-DD a recorded run started on, or null when the timestamp does not parse. */
export function runLocalDate(run: RunRecordRow): string | null {
  const parsed = new Date(run.startedAt);
  if (Number.isNaN(parsed.getTime())) return null;
  return localIsoDate(parsed);
}

/** The run's compact display marker: its state plus the first sanitised outcome field present. */
export function runMarker(run: RunRecordRow): string {
  const detail = run.outcome ?? run.error ?? run.failureClass;
  return detail === null ? run.state : `${run.state} · ${detail}`;
}

// --- MONTH WINDOW (the calendar's Previous/Next navigation) ---------------------------------------------
//
// The month the grid paints is CLIENT VIEW-STATE and nothing more: an anchor date the Previous/Next buttons
// step, used to lay out cells and to look up entries by their local day key. Stepping it computes NO
// occurrence — the entries placed in any month are the same server-projected occurrences and recorded runs
// the projection already carried (G-110). A month with none of them renders as an empty grid saying so,
// never as invented rows.

/** The `YYYY-MM` key of a Date — the calendar's month identity (client view-state only). */
export function monthKey(date: Date): string {
  return `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** The first day of the month `step` months from the anchor (-1 previous, +1 next). Day 1 avoids the
 *  end-of-month rollover a naive setMonth on the 31st produces (31 Mar → 31 Apr → 1 May). */
export function shiftMonth(anchor: Date, step: number): Date {
  return new Date(anchor.getFullYear(), anchor.getMonth() + step, 1);
}
